/**
 * Recovery helpers for stale lazy-loaded chunks.
 *
 * After a deploy the hashed JS assets on the CDN change. A browser session that
 * was opened against the previous build fails to fetch the old chunk when the
 * user navigates to a lazy() route — surfacing as a blank/white screen because
 * the route tree has no error boundary above React's Suspense. These helpers
 * force a single full reload (which pulls the fresh index.html) and guard
 * against reload loops.
 */

/** sessionStorage key holding the timestamp (ms) of the last automatic reload. */
const LAST_RELOAD_KEY = 'app:chunkReload:lastReloadAt';

/** Minimum gap between two automatic reloads (ms) — prevents reload loops. */
const RELOAD_GUARD_MS = 10_000;

/**
 * Detect whether a thrown value is a failed dynamic import / chunk-load error.
 *
 * @param error - the thrown value (Error or anything)
 * @returns true when the error looks like a chunk-load failure
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch/i.test(
    message,
  );
};

/**
 * Reload the page once to recover from a stale-chunk error.
 *
 * Guarded so it never reloads more than once per RELOAD_GUARD_MS — if the fresh
 * build is also broken we stop instead of looping forever.
 *
 * @returns true when a reload was triggered, false when suppressed by the guard
 */
export const reloadForChunkError = (): boolean => {
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
    if (now - last < RELOAD_GUARD_MS) {
      return false;
    }
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
  } catch {
    // sessionStorage may be unavailable (private mode / blocked) — fall through
    // and reload anyway; the guard is best-effort, not a correctness requirement
  }
  window.location.reload();
  return true;
};

/**
 * Install a global listener for Vite's `vite:preloadError` event, which fires
 * when a dynamically imported chunk fails to load. Calls preventDefault() so the
 * error does not additionally bubble as an unhandled rejection, then reloads once.
 *
 * @returns void
 */
export const installChunkErrorReload = (): void => {
  window.addEventListener('vite:preloadError', (event) => {
    // stop Vite from re-throwing the preload error; we recover via reload instead
    event.preventDefault();
    reloadForChunkError();
  });
};
